import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import { getProfile, StripeInfoRecord, ProfileData } from '@/api/custom';
import { ClientFeaturesRecord } from '@/api/custom';
import {
  getGlobalFlag,
  getTenantFlag,
  getUserFlag,
  type FeatureFlagRecord,
} from '@/api/feature-flags';
import type { FirmDataAccessContext, MemberProfile } from '@/api/login.v2';

export type AgenticCanvasEngine = 'v2' | 'v3';
export const AGENTIC_CANVAS_ENGINE_FLAG_SLUG = 'agentic-canvas-engine';
export const PLANNER_ENGINE_SWITCHER_FLAG_SLUG = 'planner-engine-switcher';
// ENG-3310: advisor cost basis (summary, lots) stays off until enabled per firm/advisor.
export const ADVISOR_COST_BASIS_FLAG_SLUG = 'advisor-cost-basis';

export type AuthenticatedUser = Partial<MemberProfile> & {
  user_id?: string;
  firm_data_context?: FirmDataAccessContext | null;
  [key: string]: unknown;
};

export interface UserinfoState {
  user: AuthenticatedUser | null;
  calendars: {
    calendars: any[];
    events: any[];
  };
  featureFlags: {
    profile?: ClientFeaturesRecord | null | undefined;
    stripeInfo?: StripeInfoRecord | null | undefined;
    agenticCanvasEngine?: AgenticCanvasEngine | null | undefined;
    plannerEngineSwitcher?: boolean | null | undefined;
    advisorCostBasis?: boolean | null | undefined;
    loading: boolean;
    error: boolean;
    lastFetched: number;
  };
  profileData: {
    data: ProfileData | null;
    loading: boolean;
    error: boolean;
    lastFetched: number;
  };
  deploymentFlags: {
    wealthboxEnabled?: boolean;
    statefulCanvasEnabled?: boolean;
  };
}

const initialState: UserinfoState = {
  user: null,
  calendars: {
    calendars: [],
    events: [],
  },
  featureFlags: {
    profile: undefined,
    stripeInfo: undefined,
    agenticCanvasEngine: undefined,
    plannerEngineSwitcher: undefined,
    advisorCostBasis: undefined,
    loading: false,
    error: false,
    lastFetched: 0,
  },
  profileData: {
    data: null,
    loading: false,
    error: false,
    lastFetched: 0,
  },
  deploymentFlags: {},
};

const CACHE_EXPIRY_TIME = 10 * 60 * 1000;
export const RETRY_INTERVAL = 10 * 1000;

type FeatureFlagsFetchState = Pick<
  UserinfoState['featureFlags'],
  | 'loading'
  | 'profile'
  | 'stripeInfo'
  | 'agenticCanvasEngine'
  | 'plannerEngineSwitcher'
  | 'advisorCostBasis'
  | 'error'
  | 'lastFetched'
>;

export function shouldRequestFeatureFlags(
  user: unknown,
  featureFlags: FeatureFlagsFetchState,
  now = Date.now(),
): boolean {
  if (!user || featureFlags.loading) return false;

  if (featureFlags.error) {
    return now - featureFlags.lastFetched > RETRY_INTERVAL;
  }

  return (
    featureFlags.profile === undefined ||
    featureFlags.stripeInfo === undefined ||
    featureFlags.agenticCanvasEngine === undefined ||
    featureFlags.plannerEngineSwitcher === undefined ||
    featureFlags.advisorCostBasis === undefined
  );
}

function activeUserClientId(user: unknown): string | undefined {
  if (!user || typeof user !== 'object') return undefined;
  const clientId = (user as { client_id?: unknown }).client_id;
  return typeof clientId === 'string' && clientId.trim().length > 0 ? clientId : undefined;
}

function activeUserId(user: unknown): string | undefined {
  if (!user || typeof user !== 'object') return undefined;
  const { id, user_id } = user as { id?: unknown; user_id?: unknown };
  const userId = typeof id === 'string' && id.trim().length > 0 ? id : user_id;
  return typeof userId === 'string' && userId.trim().length > 0 ? userId : undefined;
}

function parseAgenticCanvasEngine(value: unknown): AgenticCanvasEngine | null {
  if (value === 'v2' || value === 'v3') return value;

  if (typeof value === 'string') {
    const stripped = value.trim();
    if (stripped === 'v2' || stripped === 'v3') return stripped;

    try {
      return parseAgenticCanvasEngine(JSON.parse(stripped));
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== 'object') return null;
  const engine = (value as { engine?: unknown }).engine;
  return engine === 'v2' || engine === 'v3' ? engine : null;
}

function agenticCanvasEngineFromFlag(
  flag: FeatureFlagRecord | null | undefined,
): AgenticCanvasEngine | null {
  return parseAgenticCanvasEngine(flag?.value);
}

function parseEnabledFlag(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;

  if (typeof value === 'string') {
    const stripped = value.trim();
    if (stripped === 'true') return true;
    if (stripped === 'false') return false;

    try {
      return parseEnabledFlag(JSON.parse(stripped));
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== 'object') return null;
  const enabled = (value as { enabled?: unknown }).enabled;
  return typeof enabled === 'boolean' ? enabled : null;
}

function enabledFlagFromRecord(flag: FeatureFlagRecord | null | undefined): boolean | null {
  return parseEnabledFlag(flag?.value);
}

async function fetchFlagWithPrecedence({
  slug,
  clientId,
  userId,
}: {
  slug: string;
  clientId: string | undefined;
  userId: string | undefined;
}): Promise<FeatureFlagRecord | null> {
  if (clientId && userId) {
    const userFlag = await getUserFlag(slug, clientId, userId);
    if (userFlag) return userFlag;
  }

  if (clientId) {
    const tenantFlag = await getTenantFlag(slug, clientId);
    if (tenantFlag) return tenantFlag;
  }

  return getGlobalFlag(slug);
}

async function fetchAgenticCanvasEngineFlag({
  clientId,
  userId,
}: {
  clientId: string | undefined;
  userId: string | undefined;
}): Promise<AgenticCanvasEngine | null> {
  return agenticCanvasEngineFromFlag(
    await fetchFlagWithPrecedence({
      slug: AGENTIC_CANVAS_ENGINE_FLAG_SLUG,
      clientId,
      userId,
    }),
  );
}

async function fetchPlannerEngineSwitcherFlag({
  clientId,
  userId,
}: {
  clientId: string | undefined;
  userId: string | undefined;
}): Promise<boolean | null> {
  return enabledFlagFromRecord(
    await fetchFlagWithPrecedence({
      slug: PLANNER_ENGINE_SWITCHER_FLAG_SLUG,
      clientId,
      userId,
    }),
  );
}

async function fetchAdvisorCostBasisFlag({
  clientId,
  userId,
}: {
  clientId: string | undefined;
  userId: string | undefined;
}): Promise<boolean | null> {
  try {
    return enabledFlagFromRecord(
      await fetchFlagWithPrecedence({
        slug: ADVISOR_COST_BASIS_FLAG_SLUG,
        clientId,
        userId,
      }),
    );
  } catch {
    // A failed read keeps cost basis off without failing the profile or other flags.
    return null;
  }
}

function cachedProfileMatchesActiveClient({
  activeClientId,
  features,
  profileData,
}: {
  activeClientId: string | undefined;
  features: ClientFeaturesRecord | null | undefined;
  profileData: ProfileData | null;
}): boolean {
  if (!activeClientId) return false;
  const cachedClientId = features?.client_id || profileData?.id;
  return cachedClientId === activeClientId;
}

type FeatureFlagsRequestIdentity = {
  requestedClientId: string | undefined;
  requestedAdvisorUserId: string | undefined;
};

function featureFlagsRequestMatchesActiveUser(
  requestIdentity: FeatureFlagsRequestIdentity,
  user: unknown,
): boolean {
  return (
    requestIdentity.requestedClientId === activeUserClientId(user) &&
    requestIdentity.requestedAdvisorUserId === activeUserId(user)
  );
}

function resetProfileCache(state: UserinfoState) {
  state.featureFlags = {
    profile: undefined,
    stripeInfo: undefined,
    agenticCanvasEngine: undefined,
    plannerEngineSwitcher: undefined,
    advisorCostBasis: undefined,
    loading: false,
    error: false,
    lastFetched: 0,
  };
  state.profileData = {
    data: null,
    loading: false,
    error: false,
    lastFetched: 0,
  };
}

export const fetchFeatureFlags = createAsyncThunk(
  'userinfo/fetchFeatureFlags',
  async (invalidateCache: boolean | undefined, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { featureFlags, profileData, user } = state.userinfo;
    const now = Date.now();
    const activeClientId = activeUserClientId(user);
    const activeAdvisorUserId = activeUserId(user);
    const cacheMatchesActiveClient = cachedProfileMatchesActiveClient({
      activeClientId,
      features: featureFlags.profile,
      profileData: profileData.data,
    });

    // return cached profile if it exists and is not undefined
    // check if the profile data is not null and the feature flags are not undefined
    if (
      !invalidateCache &&
      cacheMatchesActiveClient &&
      featureFlags.profile !== undefined &&
      featureFlags.stripeInfo !== undefined &&
      featureFlags.agenticCanvasEngine !== undefined &&
      featureFlags.plannerEngineSwitcher !== undefined &&
      featureFlags.advisorCostBasis !== undefined &&
      profileData.data !== null &&
      now - featureFlags.lastFetched < CACHE_EXPIRY_TIME
    ) {
      // return a combined object with featureFlags and profileData
      return {
        source: 'cache' as const,
        featureFlags,
        profileData: profileData.data,
        requestedClientId: activeClientId,
        requestedAdvisorUserId: activeAdvisorUserId,
      };
    }

    // retry logic for handling API failures (rejectWithValue cases)
    if (
      featureFlags.error &&
      cacheMatchesActiveClient &&
      now - featureFlags.lastFetched < RETRY_INTERVAL // don't retry within 1 minute
    ) {
      return rejectWithValue({
        requestedClientId: activeClientId,
        requestedAdvisorUserId: activeAdvisorUserId,
      });
    }

    try {
      const [response, agenticCanvasEngine, plannerEngineSwitcher, advisorCostBasis] =
        await Promise.all([
          getProfile(),
          fetchAgenticCanvasEngineFlag({
            clientId: activeClientId,
            userId: activeAdvisorUserId,
          }),
          fetchPlannerEngineSwitcherFlag({
            clientId: activeClientId,
            userId: activeAdvisorUserId,
          }),
          fetchAdvisorCostBasisFlag({
            clientId: activeClientId,
            userId: activeAdvisorUserId,
          }),
        ]);
      // return the complete response, let the reducer handle all data.
      // The request identity lets the reducer discard a response that resolves
      // after the user has switched to a different firm or advisor.
      return {
        source: 'api' as const,
        ...response,
        agenticCanvasEngine,
        plannerEngineSwitcher,
        advisorCostBasis,
        requestedClientId: activeClientId,
        requestedAdvisorUserId: activeAdvisorUserId,
      };
    } catch {
      // Only API call exceptions are considered failures. Carry the request
      // identity so a stale failure cannot poison the active user's cache.
      return rejectWithValue({
        requestedClientId: activeClientId,
        requestedAdvisorUserId: activeAdvisorUserId,
      });
    }
  },
);

export const initializeUserFromStorage = createAsyncThunk(
  'userinfo/initializeFromStorage',
  async () => {
    try {
      const { getItemWithExpiry } = await import('@wavvest/shared-utils');
      const userStr = getItemWithExpiry('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
      return null;
    } catch (error) {
      console.error('Failed to initialize user from storage:', error);
      return null;
    }
  },
);

const userinfoSlice = createSlice({
  name: 'userinfo',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<AuthenticatedUser>) => {
      if (
        activeUserClientId(state.user) !== activeUserClientId(action.payload) ||
        activeUserId(state.user) !== activeUserId(action.payload)
      ) {
        resetProfileCache(state);
      }
      state.user = action.payload;
      state.deploymentFlags.wealthboxEnabled = enableWealthboxForTestAccount(
        action.payload.client_id,
      );
      state.deploymentFlags.statefulCanvasEnabled = enableForTestAccount(action.payload.client_id);
    },
    addCalendars: (state, action: PayloadAction<{ calendars: any[]; events: any[] }>) => {
      state.calendars = action.payload;
    },
    resetFeatureFlags: (state) => {
      resetProfileCache(state);
    },
    resetUser: (state) => {
      state.user = null;
      state.deploymentFlags = {};
      resetProfileCache(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.featureFlags.loading = true;
        state.featureFlags.error = false;
        state.profileData.loading = true;
        state.profileData.error = false;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        // Discard a response for a firm/advisor the user has since switched away from.
        if (
          !featureFlagsRequestMatchesActiveUser(
            {
              requestedClientId: action.payload.requestedClientId,
              requestedAdvisorUserId: action.payload.requestedAdvisorUserId,
            },
            state.user,
          )
        ) {
          return;
        }

        state.featureFlags.loading = false;
        state.featureFlags.error = false;

        // check if the data is from cache or API
        if (action.payload.source === 'cache') {
          // cache return case
          state.featureFlags.profile = action.payload.featureFlags.profile;
          state.featureFlags.stripeInfo = action.payload.featureFlags.stripeInfo;
          state.featureFlags.agenticCanvasEngine = action.payload.featureFlags.agenticCanvasEngine;
          state.featureFlags.plannerEngineSwitcher =
            action.payload.featureFlags.plannerEngineSwitcher;
          state.featureFlags.advisorCostBasis = action.payload.featureFlags.advisorCostBasis;
          state.profileData.data = action.payload.profileData;
        } else {
          // API return case
          state.featureFlags.profile = action.payload.features;
          state.featureFlags.stripeInfo = action.payload.stripe_info;
          state.featureFlags.agenticCanvasEngine = action.payload.agenticCanvasEngine;
          state.featureFlags.plannerEngineSwitcher = action.payload.plannerEngineSwitcher;
          state.featureFlags.advisorCostBasis = action.payload.advisorCostBasis;
          state.profileData.data = action.payload.profile;
        }

        state.featureFlags.lastFetched = Date.now();
        state.profileData.lastFetched = Date.now();
        state.profileData.error = false;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        // Ignore a failure for a firm/advisor the user has since switched away from,
        // so stale errors cannot mark the active user's cache as failed.
        // Rejections without a snapshot (unexpected throws) still fall through
        // and are recorded.
        const rejectedIdentity = action.payload as Partial<FeatureFlagsRequestIdentity> | undefined;
        if (
          rejectedIdentity &&
          !featureFlagsRequestMatchesActiveUser(
            {
              requestedClientId: rejectedIdentity.requestedClientId,
              requestedAdvisorUserId: rejectedIdentity.requestedAdvisorUserId,
            },
            state.user,
          )
        ) {
          return;
        }

        state.featureFlags.loading = false;
        state.featureFlags.error = true;
        // We don't set profile to null here as it may already have a valid value
        // Only update lastFetched to track when to retry
        state.featureFlags.lastFetched = Date.now();

        state.profileData.loading = false;
        state.profileData.error = true;
        state.profileData.lastFetched = Date.now();
      })
      .addCase(initializeUserFromStorage.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
          state.deploymentFlags.wealthboxEnabled = enableWealthboxForTestAccount(
            action.payload.client_id,
          );
          state.deploymentFlags.statefulCanvasEnabled = enableForTestAccount(
            action.payload.client_id,
          );
        }
      });
  },
});

function enableForTestAccount(clientId: string | undefined) {
  return (
    import.meta.env.DEV ||
    // In prod, enable for this specific RIA
    clientId === 'client-28a46738-6f41-4fa2-b36f-5a220abcd839'
  );
}

function enableWealthboxForTestAccount(clientId: string | undefined) {
  return (
    import.meta.env.DEV ||
    import.meta.env.MODE === 'staging' ||
    // In prod, enable for this specific RIA
    clientId === 'client-28a46738-6f41-4fa2-b36f-5a220abcd839'
  );
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const userHasFirmDataPlannerAccess = (user: unknown): boolean => {
  if (!isRecord(user)) return false;

  if (isNonEmptyString(user.firm_data_advisor_id)) return true;

  const context = user.firm_data_context;
  if (!isRecord(context)) return false;

  return Array.isArray(context.firms) && context.firms.length > 0;
};

export const { setUser, addCalendars, resetFeatureFlags, resetUser } = userinfoSlice.actions;

export const selectUser = (state: RootState) => state.userinfo.user;
export const selectCalendars = (state: RootState) => state.userinfo.calendars;
export const selectFeatureFlags = (state: RootState) => state.userinfo.featureFlags;

export const selectProfileData = (state: RootState) => state.userinfo.profileData.data;
export const selectProfileLoading = (state: RootState) => state.userinfo.profileData.loading;
export const selectProfileError = (state: RootState) => state.userinfo.profileData.error;
export const selectApexCorrespondentId = (state: RootState) =>
  state.userinfo.profileData.data?.apex_correspondent_id;
export const selectApexEnabled = (state: RootState) =>
  state.userinfo.featureFlags.profile?.apex_enabled;

export const selectCanAccessPlanner = (state: RootState) =>
  !!state.userinfo.featureFlags.profile?.canvas_enabled ||
  userHasFirmDataPlannerAccess(state.userinfo.user);

export const selectFirmDataEnabled = (state: RootState) =>
  !!state.userinfo.featureFlags.profile?.firm_data_enabled ||
  userHasFirmDataPlannerAccess(state.userinfo.user);

export const selectAdvisorPrepDashboardFlagEnabled = (state: RootState) =>
  !!state.userinfo.featureFlags.profile?.advisor_prep_dashboard_enabled;

export const selectCanAccessAdvisorPrepDashboard = (state: RootState) =>
  selectAdvisorPrepDashboardFlagEnabled(state) && selectFirmDataEnabled(state);

export const selectInvestorRecordAutogeneratedSummaryEnabled = (state: RootState) =>
  state.userinfo.featureFlags.profile?.investor_record_autogenerated_summary_enabled === true;

export const selectInvestorRecordTodoEnabled = (state: RootState) =>
  state.userinfo.featureFlags.profile?.investor_record_todo_enabled === true;

export const selectPlannerModeSelectionEnabled = (state: RootState) =>
  state.userinfo.featureFlags.agenticCanvasEngine === 'v3';

export const selectAgenticCanvasEngine = (state: RootState) =>
  state.userinfo.featureFlags.agenticCanvasEngine;

export const selectPlannerEngineSwitcherEnabled = (state: RootState) =>
  state.userinfo.featureFlags.plannerEngineSwitcher === true;

// Missing or failed flag reads stay off.
export const selectAdvisorCostBasisEnabled = (state: RootState) =>
  state.userinfo.featureFlags.advisorCostBasis === true;

export const selectHasAccess = (state: RootState, featureFlag: string) => {
  const { featureFlags } = state.userinfo;

  if (featureFlag === 'canvas_access') {
    return selectCanAccessPlanner(state);
  }

  if (featureFlag === 'advisor_prep_dashboard_access') {
    return selectCanAccessAdvisorPrepDashboard(state);
  }

  if (!featureFlags.profile) return false;

  return !!featureFlags.profile[featureFlag as keyof ClientFeaturesRecord];
};

export default userinfoSlice.reducer;
